import { Transaction, TransactionType } from '../types/Transaction';

/**
 * As transações que estão na tela, como planilha.
 *
 * 08/10: "quando estou em transações, queria poder exportar esses dados que
 * estou vendo (filtrados ou não)". O que sai é a MESMA lista que a tabela
 * mostra, na mesma ordem e com os mesmos valores: quem chama passa as linhas
 * já filtradas e as funções que a tabela usa para o nome da categoria, do
 * método e o valor. Aqui só se escreve.
 *
 * ── Por que CSV com ponto e vírgula ──────────────────────────────────────
 *
 * O Excel em português abre CSV separando por ";" e lê número com vírgula. Com
 * vírgula de separador, tudo cairia numa coluna só. O BOM na frente é o que
 * faz o Excel ler os acentos; o Google Planilhas e o Numbers aceitam os dois.
 *
 * ── A célula que começa com "=" ──────────────────────────────────────────
 *
 * Uma descrição como "=SOMA(...)" (ou vinda de um extrato, começando com "+"
 * ou "-") seria EXECUTADA como fórmula ao abrir. O apóstrofo na frente faz a
 * planilha tratar como texto. O valor não passa por isso: ele é número de
 * verdade, e é para ser somado.
 *
 * Puro: sem DOM e sem rede. Quem baixa é `baixarCsv`.
 */

export interface ComoEscrever {
  categoria: (t: Transaction) => string;
  metodo: (t: Transaction) => string;
  /** O valor que a tabela mostra para a linha (a parcela, o valor do mês). */
  valor: (t: Transaction) => number;
}

export const COLUNAS = [
  'Data', 'Compra', 'Descrição', 'Tipo', 'Categoria', 'Método', 'Parcela', 'Mensal', 'Valor',
] as const;

const diaBr = (iso: string | null | undefined): string => {
  const [a, m, d] = String(iso ?? '').slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
};

const texto = (bruto: unknown): string => {
  let s = String(bruto ?? '').replace(/[\r\n]+/g, ' ').trim();
  if (/^[=+\-@\t]/.test(s)) s = `'${s}`;
  return /[";]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** "1234,56", sem separador de milhar: é o que a planilha lê como número. */
const numero = (v: number): string => (Number.isFinite(v) ? v.toFixed(2).replace('.', ',') : '');

export const csvDasTransacoes = (linhas: Transaction[], como: ComoEscrever): string => {
  let receitas = 0;
  let despesas = 0;
  const corpo = linhas.map(t => {
    const receita = t.type === TransactionType.INCOME;
    // A data é a da OCORRÊNCIA: a parcela 3/6 vista em outubro sai em outubro,
    // e não no mês em que a compra foi lançada.
    const data = t.displayOcorrencia ? `${t.displayOcorrencia}-${t.date.slice(8, 10)}` : t.date;
    const compra = t.purchaseDate && t.purchaseDate.slice(0, 10) !== t.date.slice(0, 10) ? t.purchaseDate : '';
    const parcela = t.hasInstallments && t.installments
      ? `${t.displayInstallment ?? t.currentInstallment ?? 1}/${t.installments}` : '';
    const valor = Math.abs(como.valor(t));
    if (receita) receitas += valor; else despesas += valor;
    return [
      diaBr(data),
      diaBr(compra),
      texto(t.description),
      receita ? 'Receita' : 'Despesa',
      texto(como.categoria(t)),
      texto(como.metodo(t)),
      // Com o apóstrofo a planilha não transforma "3/6" em 3 de junho.
      parcela ? `'${parcela}` : '',
      t.isMonthly ? 'Sim' : '',
      // Despesa negativa: somar a coluna dá o saldo do que está na lista.
      numero(receita ? valor : -valor),
    ].join(';');
  });
  /*
   * O resumo, no FIM e depois de uma linha em branco (08/10).
   *
   * No fim para o cabeçalho continuar na primeira linha, que é onde a
   * planilha espera para filtrar e ordenar. O rótulo fica na coluna da
   * descrição e o número na do valor, alinhado com o que ele soma. O total é
   * a soma da coluna Valor deste arquivo, nem mais nem menos.
   */
  const resumo = (rotulo: string, valor: string) => `;;${rotulo};;;;;;${valor}`;
  const pe = [
    '',
    resumo('Transações', String(linhas.length)),
    // Receitas e despesas só quando há as duas: com uma só, repetiriam o total.
    ...(receitas > 0 && despesas > 0
      ? [resumo('Receitas', numero(receitas)), resumo('Despesas', numero(-despesas))] : []),
    resumo('Total', numero(receitas - despesas)),
  ];
  return `\uFEFF${[COLUNAS.join(';'), ...corpo, ...pe].join('\r\n')}\r\n`;
};

/** Entrega o arquivo ao navegador. */
export const baixarCsv = (conteudo: string, nome: string): void => {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
